import { Component, OnInit } from '@angular/core';
import { TitleCasePipe } from '@angular/common';
import { ThemePalette } from '@angular/material';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { saveAs } from 'file-saver';
import { Subject, timer } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { map } from 'rxjs/operators/map';
import { matSpinnerOptions } from 'src/app/authentication/authenticator-modal/authenticator-modal.component';
import { Html2PdfService } from 'src/app/core/api/html-2-pdf.service';
import { PatientResponse } from '../patient-portal-models/patient-portal.model';
import { PatientPortalService } from '../patient-portal.service';
interface MatProgressButtonIcon {
  color?: ThemePalette;
  fontIcon?: string;
  fontSet?: string;
  inline?: boolean;
  svgIcon?: string;
  customClass?: string;
}

@Component({
  selector: 'csi-patient-portal-view',
  templateUrl: './patient-portal-view.component.html',
  styleUrls: ['./patient-portal-view.component.scss']
})
export class PatientPortalViewComponent implements OnInit {
  public remainingTime: string;
  public targetDate: Date;
  public patient: any = this.patientPortalService.patientInformation;
  private destroyed$ = new Subject<void>();

  public matSpinnerOptions = { ...matSpinnerOptions };
  public matProgressButtonIcon: MatProgressButtonIcon = {
    fontIcon: 'download',
    inline: false,
    customClass: 'download-icon'
  };

  constructor(
    private sanitizer: DomSanitizer,
    private patientPortalService: PatientPortalService,
    private html2PdfService: Html2PdfService,
    public translocoService: TranslocoService,
    private router: Router
  ) {}

  ngOnInit() {
    this.matSpinnerOptions.buttonIcon = this.matProgressButtonIcon;
    this.translocoService
      .selectTranslate('Download')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(text => {
        this.matSpinnerOptions.text = text || 'Download';
      });
    this.matSpinnerOptions.text = 'Download';
    this.matSpinnerOptions.customClass = 'download-btn';
    const urlData = this.patientPortalService.parseUrlDate();
    if (!urlData.amzDate) {
      this.router.navigate([
        'patient-portal',
        this.patientPortalService.setPatientPortalId,
        'verify'
      ]);
      return;
    }

    this.targetDate = urlData.amzDate;
    this.targetDate = new Date(urlData.amzDate.getTime() + +urlData.expires * 1000);

    timer(0, 1000)
      .pipe(
        map(() => this.getTimeDifference(this.targetDate)),
        takeUntil(this.destroyed$)
      )
      .subscribe(time => {
        this.remainingTime = time;
        if (time === '00:00:00') {
          this.router.navigate(['/login']);
        }
      });
  }

  download() {
    if (this.matSpinnerOptions.active) return;
    this.matSpinnerOptions.active = true;
    const createdAt = this.patient.pdfMetadata && this.patient.pdfMetadata.createdAt;
    const createdAtPart = createdAt ? ' ' + new Date(createdAt).toISOString().split('T')[0] : '';
    const titleCasePipe = new TitleCasePipe();
    const fileName =
      titleCasePipe.transform(this.patient.firstName) +
      ' ' +
      titleCasePipe.transform(this.patient.lastName) +
      createdAtPart +
      '.pdf';
    this.patientPortalService
      .getPatientAssessmentBase64(this.patientPortalService._savedPdfLink)
      .pipe(debounceTime(500), takeUntil(this.destroyed$))
      .subscribe({
        next: blob => {
          saveAs(blob, fileName);
          this.matSpinnerOptions.active = false;
        },
        error: error => {
          console.log(error);
          this.matSpinnerOptions.active = false;
        }
      });
  }
  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  get timeUrgency(): 'normal' | 'warning' | 'critical' {
    if (!this.targetDate) return 'normal';
    const secondsLeft = (this.targetDate.getTime() - new Date().getTime()) / 1000;
    if (secondsLeft <= 60) return 'critical'; // last 1 min
    if (secondsLeft <= 300) return 'warning'; // last 5 mins
    return 'normal';
  }

  private getTimeDifference(targetDate: Date): string {
    const difference = targetDate.getTime() - new Date().getTime();
    if (difference <= 0) return '00:00:00';

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    // Format to ensure two digits for consistent display
    const formatUnit = (unit: number) => (unit < 10 ? `0${unit}` : unit.toString());

    if (days > 0) {
      return `${days}d ${formatUnit(hours)}:${formatUnit(minutes)}:${formatUnit(seconds)}`;
    }
    return `${formatUnit(hours)}:${formatUnit(minutes)}:${formatUnit(seconds)}`;
  }
}
